/**
 * ClinicSelectionSection Component
 * Handles clinic search and selection functionality
 */

import React from 'react';
import { Input } from '@/components/ui/input';
import { Search } from 'lucide-react';
import { useClickOutside } from '@/hooks/useClickOutside';

interface Clinic {
  clinic_id: number;
  clinic_name: string;
  patient_clinic_timezone: string;
}

interface ClinicSelectionSectionProps {
  // State
  clinicSearchTerm: string;
  showClinicDropdown: boolean;
  clinics: Clinic[];
  selectedClinic: string;
  
  // Actions
  onClinicSearchChange: (value: string) => void;
  onShowClinicDropdown: (show: boolean) => void;
  onClinicSelect: (clinicId: string, clinicName: string, timezone?: string) => void;
}

export const ClinicSelectionSection: React.FC<ClinicSelectionSectionProps> = ({
  clinicSearchTerm,
  showClinicDropdown,
  clinics,
  selectedClinic,
  onClinicSearchChange,
  onShowClinicDropdown,
  onClinicSelect
}) => {
  const filteredClinics = clinics.filter(clinic =>
    clinic.clinic_name.toLowerCase().includes(clinicSearchTerm.toLowerCase())
  );

  // Handle click outside to close dropdown
  const dropdownRef = useClickOutside<HTMLDivElement>(() => {
    if (showClinicDropdown) {
      onShowClinicDropdown(false);
    }
  });

  return (
    <div>
      <label className="block text-sm font-medium text-foreground mb-2">
        Select Clinic*
      </label>
      <div className="relative" ref={dropdownRef}>
        <div className="relative">
          <Input
            placeholder="Search clinics..."
            value={clinicSearchTerm}
            onChange={(e) => onClinicSearchChange(e.target.value)}
            onFocus={() => onShowClinicDropdown(true)}
            className="w-full"
          />
          <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        </div>
        {showClinicDropdown && (
          <div className="absolute z-50 w-full mt-1 bg-background border border-border rounded-lg shadow-lg max-h-60 overflow-y-auto">
            {filteredClinics.map((clinic) => (
              <div
                key={clinic.clinic_id}
                className="px-4 py-2 hover:bg-accent cursor-pointer border-b border-border last:border-b-0 bg-background"
                onClick={() => {
                  onClinicSelect(
                    clinic.clinic_id.toString(),
                    clinic.clinic_name,
                    clinic.patient_clinic_timezone
                  );
                }}
              >
                <div className="text-sm text-foreground truncate" title={clinic.clinic_name}>{clinic.clinic_name}</div>
              </div>
            ))}
            {filteredClinics.length === 0 && (
              <div className="px-4 py-2 text-sm text-muted-foreground bg-background">
                No clinics found
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};